import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
  selectDrinkById, 
  selectAllFlavors, 
  selectAllAttributes, 
  selectBrandById, 
  updateDrinkCommunityEdits,
  selectGlobalDataLoading,
  selectGlobalDataInitialized,
  selectGlobalDataError,
  fetchDrinkById,
  selectFetchDrinkByIdStatus,
  selectFetchDrinkByIdError
} from '@/store/slices/globalDataSlice';
import type { DrinkFlavor, DrinkAttribute, Drink } from '@/types/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { X, ArrowDownUp, RefreshCw, Loader2 } from 'lucide-react';
import StarRating from '@/components/ui/star-rating';
import { selectUser } from '@/store/slices/authSlice';
import { Card } from '@/components/ui/card';
import {
  setupReviewsListenerForActiveDrink,
  selectAllActiveDrinkReviews,
  selectActiveDrinkLoadingStatus,
  selectActiveDrinkError,
  clearActiveDrinkData,
  ReduxReview
} from '@/store/slices/activeDrinkSlice';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EditorInteractable } from '@/components/editor/EditorInteractable';

// Helper function to sort items by group and orderInGroup
// Ensures that T has the properties we need from Flavor/Attribute
const sortGroupableItems = <T extends { id: string; name: string; groupId?: number | null; orderInGroup?: number | null }>(items: ReadonlyArray<T>): T[] => {
  return [...items].sort((a, b) => {
    const groupA = a.groupId !== undefined && a.groupId !== null ? String(a.groupId) : 'Uncategorized';
    const groupB = b.groupId !== undefined && b.groupId !== null ? String(b.groupId) : 'Uncategorized';
    const orderA = a.orderInGroup ?? Infinity;
    const orderB = b.orderInGroup ?? Infinity;

    if (groupA < groupB) return -1;
    if (groupA > groupB) return 1;
    if (orderA < orderB) return -1;
    if (orderA > orderB) return 1;
    return a.name.localeCompare(b.name);
  });
};

type ReviewSortField = 'date' | 'rating';

interface DrinkDetailsModalProps {
  drinkId: string;
  onClose: () => void;
}

const DrinkDetailsModal: React.FC<DrinkDetailsModalProps> = ({ drinkId, onClose }) => {
  const dispatch = useAppDispatch();
  const drinkFromStore = useAppSelector(state => selectDrinkById(state, drinkId));
  const brandFromStore = useAppSelector(state => drinkFromStore ? selectBrandById(state, drinkFromStore.brandId) : undefined);
  const allGlobalFlavors = useAppSelector(selectAllFlavors);
  const allGlobalAttributes = useAppSelector(selectAllAttributes);
  const authenticatedUser = useAppSelector(selectUser);

  // Global data selectors
  const globalLoading = useAppSelector(selectGlobalDataLoading);
  const globalInitialized = useAppSelector(selectGlobalDataInitialized);
  const globalError = useAppSelector(selectGlobalDataError);

  // Selectors for specific fetch operation
  const fetchSpecificDrinkStatus = useAppSelector(selectFetchDrinkByIdStatus);
  const fetchSpecificDrinkError = useAppSelector(selectFetchDrinkByIdError);

  // Local state for the drink if it needs to be fetched specifically
  // This is primarily to bridge the gap if the store updates slightly after fetchDrinkById resolves.
  // The main source of truth should become drinkFromStore once the store is updated.
  const [localFetchedDrink, setLocalFetchedDrink] = useState<Drink | null>(null);

  const reviews = useAppSelector(selectAllActiveDrinkReviews) as ReduxReview[]; 
  const reviewsLoadingStatus = useAppSelector(selectActiveDrinkLoadingStatus);
  const reviewsError = useAppSelector(selectActiveDrinkError);

  const [reviewSortField, setReviewSortField] = useState<ReviewSortField>('date');
  const [reviewSortDirection, setReviewSortDirection] = useState<'asc' | 'desc'>('desc');
  const [reviewSearchTerm, setReviewSearchTerm] = useState<string>('');

  const [selectedFlavorIds, setSelectedFlavorIds] = useState<string[]>([]);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<string[]>([]);
  const [isUpdatingCommunityInfo, setIsUpdatingCommunityInfo] = useState<boolean>(false);

  const [showNotFoundErrorWithTimeout, setShowNotFoundErrorWithTimeout] = useState(false);

  // Add this at the component level with the other state declarations
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const reviewsContainerRef = useRef<HTMLDivElement>(null);

  // Determine the active drink to display
  const activeDrink = drinkFromStore || localFetchedDrink;
  const activeBrand = brandFromStore || (localFetchedDrink ? { id: localFetchedDrink.brandId, name: localFetchedDrink.brandName, brandSize: localFetchedDrink.brandSize } : undefined);

  useEffect(() => {
    if (activeDrink) {
      setSelectedFlavorIds(activeDrink.flavors?.map(f => f.id) || []);
      setSelectedAttributeIds(activeDrink.attributes?.map(a => a.id) || []);
      // If drinkFromStore becomes available, prefer it and clear localFetchedDrink
      if (drinkFromStore && localFetchedDrink && drinkFromStore.id === localFetchedDrink.id) {
        setLocalFetchedDrink(null);
      }
    }
  }, [activeDrink, drinkFromStore, localFetchedDrink]);

  useEffect(() => {
    if (drinkId) {
      dispatch(setupReviewsListenerForActiveDrink({ drinkId, currentUserId: authenticatedUser?.id }));
      
      // Reset states for the new drinkId
      setShowNotFoundErrorWithTimeout(false);
      setLocalFetchedDrink(null); // Important: clear local fetch for new ID

      // Fetch if not in store and global data is initialized
      if (globalInitialized && !drinkFromStore) {
        console.log(`[DrinkDetailsModal] Drink ${drinkId} not in store, global initialized. Fetching...`);
        dispatch(fetchDrinkById(drinkId)).then(action => {
          if (fetchDrinkById.fulfilled.match(action) && action.payload) {
            // If a new drink is selected quickly, this might apply to the old one if not careful.
            // Check if the fetched drink is for the CURRENT drinkId.
            if (action.meta.arg === drinkId) { 
              setLocalFetchedDrink(action.payload);
            }
          } else if (fetchDrinkById.rejected.match(action) && action.meta.arg === drinkId) {
            console.error("[DrinkDetailsModal] Specific fetch failed from useEffect", action.payload);
          }
        });
      }
    }
    return () => {
      dispatch(clearActiveDrinkData());
    };
  }, [dispatch, drinkId, authenticatedUser?.id, globalInitialized, drinkFromStore]); // Added drinkFromStore

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined;
    // If global data is loaded, and we tried to fetch specifically, but still no activeDrink
    if (globalInitialized && fetchSpecificDrinkStatus === 'succeeded' && !activeDrink) {
        setShowNotFoundErrorWithTimeout(true);
    } else if (globalInitialized && fetchSpecificDrinkStatus === 'idle' && !activeDrink && !drinkFromStore) {
        // If global is initialized, status is idle (meaning no fetch was attempted or completed for *this specific ID* via this modal instance yet)
        // and still no drink, start a timer for not found. This covers cases where fetch was initiated by another component.
        timerId = setTimeout(() => {
            if (!activeDrink) { // Re-check after timeout
                 setShowNotFoundErrorWithTimeout(true);
            }
        }, 3000); // Slightly shorter timeout if we assume global data is there
    } else if (activeDrink) {
        setShowNotFoundErrorWithTimeout(false);
    }

    return () => clearTimeout(timerId);
  }, [activeDrink, globalInitialized, fetchSpecificDrinkStatus, drinkId, drinkFromStore]);

  const handleToggleFlavor = async (flavorId: string) => {
    if (!activeDrink) return;

    const currentlySelected = selectedFlavorIds.includes(flavorId);
    const newSelectedIds = currentlySelected
      ? selectedFlavorIds.filter(id => id !== flavorId)
      : [...selectedFlavorIds, flavorId];

    setIsUpdatingCommunityInfo(true);

    const newFlavors: DrinkFlavor[] = newSelectedIds.map(id => {
      const globalFlavor = allGlobalFlavors.find(f => f.id === id);
      return { 
        id: id, 
        name: globalFlavor?.name || 'Unknown Flavor', 
        groupId: globalFlavor?.groupId ?? 0,
        orderInGroup: globalFlavor?.orderInGroup ?? 0
      };
    });
    const currentAttributes: DrinkAttribute[] = selectedAttributeIds.map(id => {
      const globalAttr = allGlobalAttributes.find(a => a.id === id);
      return { 
        id: id, 
        name: globalAttr?.name || 'Unknown Attribute',
        groupId: globalAttr?.groupId ?? 0,
        orderInGroup: globalAttr?.orderInGroup ?? 0
      };
    });

    try {
      await dispatch(updateDrinkCommunityEdits({
        drinkId: activeDrink.id,
        flavors: newFlavors,
        attributes: currentAttributes,
      })).unwrap();
      setSelectedFlavorIds(newSelectedIds); // Update local state on success
    } catch (error) {
      console.error("Failed to update flavor:", error);
      alert("Failed to save flavor change. Please try again.");
    } finally {
      setIsUpdatingCommunityInfo(false);
    }
  };

  const handleToggleAttribute = async (attributeId: string) => {
    if (!activeDrink) return;

    const currentlySelected = selectedAttributeIds.includes(attributeId);
    const newSelectedIds = currentlySelected
      ? selectedAttributeIds.filter(id => id !== attributeId)
      : [...selectedAttributeIds, attributeId];

    setIsUpdatingCommunityInfo(true);

    const newAttributes: DrinkAttribute[] = newSelectedIds.map(id => {
      const globalAttr = allGlobalAttributes.find(a => a.id === id);
      return { 
        id: id, 
        name: globalAttr?.name || 'Unknown Attribute',
        groupId: globalAttr?.groupId ?? 0,
        orderInGroup: globalAttr?.orderInGroup ?? 0
      };
    });
    const currentFlavors: DrinkFlavor[] = selectedFlavorIds.map(id => {
      const globalFlavor = allGlobalFlavors.find(f => f.id === id);
      return { 
        id: id, 
        name: globalFlavor?.name || 'Unknown Flavor',
        groupId: globalFlavor?.groupId ?? 0,
        orderInGroup: globalFlavor?.orderInGroup ?? 0
      };
    });

    try {
      await dispatch(updateDrinkCommunityEdits({
        drinkId: activeDrink.id,
        flavors: currentFlavors,
        attributes: newAttributes,
      })).unwrap();
      setSelectedAttributeIds(newSelectedIds); // Update local state on success
    } catch (error) {
      console.error("Failed to update attribute:", error);
      alert("Failed to save attribute change. Please try again.");
    } finally {
      setIsUpdatingCommunityInfo(false);
    }
  };

  const filteredAndSortedReviews = useMemo(() => {
    let processed = [...reviews]; 
    if (authenticatedUser?.id) {
      processed = processed.filter(review => {
        return review.isPublic || review.userId !== authenticatedUser.id;
      });
    }
    if (reviewSearchTerm) {
      const lowerSearch = reviewSearchTerm.toLowerCase();
      processed = processed.filter(r =>
        (r.reviewText ?? '').toLowerCase().includes(lowerSearch) ||
        r.username.toLowerCase().includes(lowerSearch) 
      );
    }
    processed.sort((a, b) => {
      let comparison = 0;
      if (reviewSortField === 'date') {
        comparison = b.date - a.date; 
      } else if (reviewSortField === 'rating') {
        comparison = b.rating - a.rating;
      }
      return reviewSortDirection === 'asc' ? comparison * -1 : comparison;
    });
    return processed;
  }, [reviews, reviewSearchTerm, reviewSortField, reviewSortDirection, authenticatedUser]);

  const handleToggleReviewSortDirection = () => {
    setReviewSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleRetryFetch = () => {
    setShowNotFoundErrorWithTimeout(false);
    setLocalFetchedDrink(null); // Clear local attempt
    // Re-dispatch the specific fetch. If global data isn't init, it will be handled by App.tsx or subsequent logic.
    dispatch(fetchDrinkById(drinkId)).then(action => {
        if (fetchDrinkById.fulfilled.match(action) && action.payload && action.meta.arg === drinkId) {
            setLocalFetchedDrink(action.payload);
        }
    });
  };

  // Add this useEffect before the return statement
  useEffect(() => {
    const checkForScrollbar = () => {
      if (reviewsContainerRef.current) {
        const element = reviewsContainerRef.current;
        const hasVerticalScrollbar = element.scrollHeight > element.clientHeight;
        setHasScrollbar(hasVerticalScrollbar);
      }
    };
    
    checkForScrollbar();
    
    // Set up resize observer to recheck when container size changes
    const resizeObserver = new ResizeObserver(checkForScrollbar);
    
    if (reviewsContainerRef.current) {
      resizeObserver.observe(reviewsContainerRef.current);
    }
    
    return () => {
      if (reviewsContainerRef.current) {
        resizeObserver.unobserve(reviewsContainerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [filteredAndSortedReviews.length]);

  // --- Loading and Error UI States ---

  // 1. Global data is initializing for the very first time
  if (globalLoading === 'pending' && !globalInitialized) {
    return (
      <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Initializing Data...</DialogTitle>
            <DialogDescription className="sr-only">Application data is initializing.</DialogDescription>
          </DialogHeader>
          <p>Loading application data, please wait...</p>
        </DialogContent>
      </Dialog>
    );
  }

  // 2. Global data initialization failed
  if (globalLoading === 'failed' && !globalInitialized && globalError) {
    return (
      <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Error Loading Data</DialogTitle>
            <DialogDescription className="sr-only">Error loading application data.</DialogDescription>
          </DialogHeader>
          <p>Could not load initial application data: {globalError}</p>
          {/* Optionally, a button to retry global initialization might be too broad here
              as App.tsx is responsible for global init. This modal should focus on the specific drink. */}
        </DialogContent>
      </Dialog>
    );
  }

  // 3. Specific drink fetch is pending (and no drink data yet)
  if (fetchSpecificDrinkStatus === 'pending' && !activeDrink) {
    return (
      <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Fetching Drink Details...</DialogTitle>
            <DialogDescription className="sr-only">Fetching details for drink ID {drinkId}.</DialogDescription>
          </DialogHeader>
          <p>Fetching details for drink ID "{drinkId}", please wait...</p>
        </DialogContent>
      </Dialog>
    );
  }

  // 4. Specific drink fetch failed (and no drink data yet)
  if (fetchSpecificDrinkStatus === 'failed' && !activeDrink && fetchSpecificDrinkError) {
    return (
      <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Error Loading Drink</DialogTitle>
            <DialogDescription className="sr-only">Error fetching drink details for ID {drinkId}.</DialogDescription>
          </DialogHeader>
          <p>There was an error fetching the drink details: {fetchSpecificDrinkError}</p>
          <div className="flex justify-end mt-4">
            <Button onClick={handleRetryFetch} className="gap-2">
              <RefreshCw size={16} />
              Retry Fetch
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // 5. Drink not found after timeout or confirmed not found from specific fetch (and no drink data)
  if (showNotFoundErrorWithTimeout && !activeDrink) { 
    return (
      <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Drink Not Found</DialogTitle>
            <DialogDescription className="sr-only">Drink with ID {drinkId} not found.</DialogDescription>
          </DialogHeader>
          <p>Sorry, the details for the drink with ID "{drinkId}" could not be found.</p>
          <div className="flex justify-end mt-4">
            <Button onClick={handleRetryFetch} className="gap-2">
              <RefreshCw size={16} />
              Retry
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // 6. Fallback loading state if global data is initialized but specific drink isn't yet in store or fetched, 
  //    and no specific fetch is pending/failed for it yet.
  if (globalInitialized && !activeDrink && fetchSpecificDrinkStatus !== 'pending' && fetchSpecificDrinkStatus !== 'failed') {
    return (
      <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Loading Drink Details...</DialogTitle>
            <DialogDescription className="sr-only">Preparing details for drink ID {drinkId}.</DialogDescription>
          </DialogHeader>
          <p>Preparing details for drink ID "{drinkId}", please wait...</p>
        </DialogContent>
      </Dialog>
    );
  }

  // If activeDrink is still null here, it means we haven't hit any of the above specific loading/error/not found states
  // but the drink isn't available. This path should ideally be covered by the states above.
  // However, as a final fallback if activeDrink is somehow still null:
  if (!activeDrink) {
    return (
        <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Preparing Drink...</DialogTitle>
                    <DialogDescription className="sr-only">Preparing drink details.</DialogDescription>
                </DialogHeader>
                <p>Please wait while we prepare the drink information.</p>
            </DialogContent>
        </Dialog>
    );
  }

  // Main component render once activeDrink is available
  return (
    <Dialog open={true} modal={true} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl lg:max-w-4xl w-[95vw] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-1 sticky top-0 bg-background z-10">
          <div className="flex justify-between items-start">
            <div className="flex-grow min-w-0 pr-4">
              <DialogTitle className="text-2xl font-bold break-words">
                <span className="text-muted-foreground">{activeDrink.brandName || activeBrand?.name || 'Unknown Brand'}</span>{' '}
                <span className="text-primary">{activeDrink.name}</span>
              </DialogTitle>
              <DialogDescription className="sr-only">
                Details for {activeDrink.brandName || activeBrand?.name || 'Unknown Brand'} {activeDrink.name}, including community ratings, reviews, flavors, and attributes.
              </DialogDescription>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {selectedFlavorIds.length > 0 && selectedFlavorIds.map(id => {
                    const flavor = allGlobalFlavors.find(f => f.id === id) || activeDrink.flavors?.find(f => f.id === id);
                    return <Badge key={`header-badge-flavor-${id}`} variant="default" className="text-xs font-normal">{flavor?.name || 'Unknown'}</Badge>;
                })}
                {selectedAttributeIds.length > 0 && selectedAttributeIds.map(id => {
                    const attr = allGlobalAttributes.find(a => a.id === id) || activeDrink.attributes?.find(a => a.id === id);
                    return <Badge key={`header-badge-attr-${id}`} variant="outline" className="text-xs font-normal hover:border-primary hover:text-primary">{attr?.name || 'Unknown'}</Badge>;
                })}
                {selectedFlavorIds.length === 0 && selectedAttributeIds.length === 0 && (
                    <span className="text-xs text-muted-foreground italic pt-1">No community-added tags yet.</span>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3 flex-shrink-0">
              <div className="flex flex-col items-end gap-2">
                {activeDrink.reviewCount && activeDrink.reviewCount > 0 ? (
                  <div className="flex flex-col items-end">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <StarRating value={activeDrink.averageRating || 0} readOnly size="lg" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="text-sm w-max">
                          <div className="flex flex-col space-y-0.5">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground mr-2">Average:</span>
                              <span className="font-semibold text-right">{activeDrink.averageRating?.toFixed(1) ?? 'N/A'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground mr-2">Raters:</span>
                              <span className="font-semibold text-right">{activeDrink.reviewCount ?? 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground mr-2">Public reviews:</span>
                              <span className="font-semibold text-right">{activeDrink.publicReviewCount ?? 0}</span>
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ) : (
                  <div className="text-base text-muted-foreground text-right min-h-12 flex items-center justify-end">
                    No ratings yet
                  </div>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X size={20} /><span className="sr-only">Close</span>
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-grow overflow-y-auto p-6 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 flex flex-col space-y-6 h-[60vh] pr-2">
            <section className="flex flex-col h-full">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 flex-shrink-0 sticky top-0 bg-background z-10 pb-2">
                <h3 className="text-xl font-semibold text-muted-foreground">Reviews ({filteredAndSortedReviews.length})</h3>
                <div className="flex gap-2 items-center w-full sm:w-auto">
                  <Input
                    type="text"
                    placeholder="Search reviews..."
                    value={reviewSearchTerm}
                    onChange={(e) => setReviewSearchTerm(e.target.value)}
                    className="h-9 text-sm flex-grow sm:flex-grow-0 sm:w-48"
                  />
                  <Select value={reviewSortField} onValueChange={(value: ReviewSortField) => setReviewSortField(value)}>
                    <SelectTrigger className="h-9 text-sm w-auto min-w-[100px]"><SelectValue placeholder="Sort by" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="rating">Rating</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={handleToggleReviewSortDirection} className="h-9 w-9 flex-shrink-0">
                    <ArrowDownUp size={14} className={reviewSortDirection === 'desc' ? 'rotate-180 transform' : ''} />
                  </Button>
                </div>
              </div>

              <div 
                ref={reviewsContainerRef} 
                className={`overflow-y-auto flex-grow ${hasScrollbar ? 'pr-3' : ''}`}>
                {reviewsLoadingStatus === 'pending' && <p className="text-muted-foreground py-4 text-center">Loading reviews...</p>}
                {reviewsLoadingStatus === 'failed' && <p className="text-destructive py-4 text-center">Error loading reviews: {reviewsError}</p>}
                {reviewsLoadingStatus === 'succeeded' && filteredAndSortedReviews.length === 0 && (
                  <p className="text-muted-foreground py-4 text-center">
                    {reviewSearchTerm ? 'No reviews match your search.' : `No reviews for ${activeDrink.name} yet.`}
                  </p>
                )}
                {reviewsLoadingStatus === 'succeeded' && filteredAndSortedReviews.length > 0 && (
                  <div className="space-y-4 pb-2">
                    {filteredAndSortedReviews.map((review: ReduxReview) => (
                      <EditorInteractable 
                        key={review.id} 
                        entityId={review.id} 
                        entityType="review" 
                        requiredPermission="editor"
                        className="w-full block"
                      >
                        <Card className="bg-card/50 p-3 gap-3 shadow w-full">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{review.username}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(review.date).toLocaleDateString()} - {new Date(review.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <StarRating value={review.rating} readOnly size="sm" />
                          </div>
                          <p className="text-sm text-card-foreground/90 whitespace-pre-wrap break-words">{review.reviewText}</p>
                        </Card>
                      </EditorInteractable>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="md:col-span-1 space-y-0 md:sticky md:self-start md:max-h-[calc(100vh-var(--dialog-header-height,100px)-3rem)] md:overflow-y-auto md:pr-2">            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="flavors">
                <AccordionTrigger className="text-base font-medium hover:no-underline py-3">
                  <div className="flex items-center">
                    {isUpdatingCommunityInfo && <Loader2 size={16} className="animate-spin mr-2" />}
                    Edit Flavors ({selectedFlavorIds.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="overflow-y-auto espacio-y-1.5 pt-1 pb-2 pr-1">
                  {allGlobalFlavors.length > 0 ? (() => {
                    const sortedFlavors = sortGroupableItems(allGlobalFlavors);
                    let lastGroupString: string | null = null;
                    return sortedFlavors.map((flavor) => {
                      const currentGroupString = flavor.groupId !== undefined && flavor.groupId !== null ? String(flavor.groupId) : 'Uncategorized';
                      const showSeparator = lastGroupString !== null && currentGroupString !== lastGroupString;
                      if (showSeparator) {
                        lastGroupString = currentGroupString;
                      } else if (lastGroupString === null) {
                        lastGroupString = currentGroupString;
                      }
                      return (
                        <React.Fragment key={flavor.id}>
                          {showSeparator && <Separator className="my-2" key={`flavor-sep-${currentGroupString}-${flavor.id}`} />}
                          <div className="flex items-center space-x-2 py-1">
                            <Checkbox
                              id={`flavor-${flavor.id}`}
                              checked={selectedFlavorIds.includes(flavor.id)}
                              onCheckedChange={() => handleToggleFlavor(flavor.id)}
                              aria-labelledby={`flavor-label-${flavor.id}`}
                              disabled={isUpdatingCommunityInfo}
                            />
                            <Label htmlFor={`flavor-${flavor.id}`} id={`flavor-label-${flavor.id}`} className="text-sm font-normal leading-none cursor-pointer">
                              {flavor.name}
                            </Label>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })() : <p className="text-xs text-muted-foreground px-1 py-2">No global flavors defined.</p>}
                </AccordionContent>
              </AccordionItem>
              <Separator className="my-0" /> 
              <AccordionItem value="attributes">
                <AccordionTrigger className="text-base font-medium hover:no-underline py-3">
                  <div className="flex items-center">
                    {isUpdatingCommunityInfo && <Loader2 size={16} className="animate-spin mr-2" />}
                    Edit Attributes ({selectedAttributeIds.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="overflow-y-auto space-y-1.5 pt-1 pb-2 pr-1">
                  {allGlobalAttributes.length > 0 ? (() => {
                    const sortedAttributes = sortGroupableItems(allGlobalAttributes);
                    let lastGroupString: string | null = null;
                    return sortedAttributes.map((attr) => {
                      const currentGroupString = attr.groupId !== undefined && attr.groupId !== null ? String(attr.groupId) : 'Uncategorized';
                      const showSeparator = lastGroupString !== null && currentGroupString !== lastGroupString;
                      if (showSeparator) {
                        lastGroupString = currentGroupString;
                      } else if (lastGroupString === null) {
                        lastGroupString = currentGroupString;
                      }
                      return (
                        <React.Fragment key={attr.id}>
                          {showSeparator && <Separator className="my-2" key={`attr-sep-${currentGroupString}-${attr.id}`} />}
                          <div className="flex items-center space-x-2 py-1">
                            <Checkbox
                              id={`attr-${attr.id}`}
                              checked={selectedAttributeIds.includes(attr.id)}
                              onCheckedChange={() => handleToggleAttribute(attr.id)}
                              aria-labelledby={`attr-label-${attr.id}`}
                              disabled={isUpdatingCommunityInfo}
                            />
                            <Label htmlFor={`attr-${attr.id}`} id={`attr-label-${attr.id}`} className="text-sm font-normal leading-none cursor-pointer">
                              {attr.name}
                            </Label>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })() : <p className="text-xs text-muted-foreground px-1 py-2">No global attributes defined.</p>}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DrinkDetailsModal; 